import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, MessageSquare, Settings, Plus, Edit, Trash2 } from 'lucide-react';

export const Dashboard = () => {
  const { user } = useAuth();
  const [characters, setCharacters] = useState([]);
  const [stats, setStats] = useState({
    totalCharacters: 0,
    totalMessages: 0,
    activeServers: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Mock data - replace with actual API call
    setTimeout(() => {
      setCharacters([
        {
          id: 1,
          name: 'Character 1',
          avatar: 'https://cdn.discordapp.com/embed/avatars/0.png',
          posts: 150,
          lastActive: '2 hours ago'
        },
        {
          id: 2,
          name: 'Character 2', 
          avatar: 'https://cdn.discordapp.com/embed/avatars/1.png',
          posts: 89,
          lastActive: '1 day ago'
        }
      ]);
      
      setStats({
        totalCharacters: 2,
        totalMessages: 239,
        activeServers: 3
      });
      
      setLoading(false);
    }, 1000);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-discord-blurple"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="discord-card">
        <h1 className="text-2xl font-bold text-white mb-2">
          Welcome back, {user?.username}!
        </h1>
        <p className="text-discord-light">
          Manage your Storybook characters and settings from here.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="discord-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-discord-light text-sm">Total Characters</p>
              <p className="text-2xl font-bold text-white">{stats.totalCharacters}</p>
            </div>
            <User className="w-8 h-8 text-discord-blurple" />
          </div>
        </div>

        <div className="discord-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-discord-light text-sm">Total Messages</p>
              <p className="text-2xl font-bold text-white">{stats.totalMessages}</p>
            </div>
            <MessageSquare className="w-8 h-8 text-discord-green" />
          </div>
        </div>

        <div className="discord-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-discord-light text-sm">Active Servers</p>
              <p className="text-2xl font-bold text-white">{stats.activeServers}</p>
            </div>
            <Settings className="w-8 h-8 text-discord-yellow" />
          </div>
        </div>
      </div>

      {/* Recent Characters */}
      <div className="discord-card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Recent Characters</h2>
          <button className="discord-button flex items-center space-x-1">
            <Plus className="w-4 h-4" />
            <span>Add Character</span>
          </button>
        </div>

        <div className="space-y-4">
          {characters.map((character) => (
            <div key={character.id} className="flex items-center justify-between p-4 bg-discord-darker rounded-lg">
              <div className="flex items-center space-x-4">
                <img 
                  src={character.avatar}
                  alt={character.name}
                  className="w-12 h-12 rounded-full"
                />
                <div>
                  <h3 className="font-medium text-white">{character.name}</h3>
                  <p className="text-sm text-discord-light">
                    {character.posts} messages • Last active {character.lastActive}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button className="p-2 text-discord-light hover:text-white transition-colors">
                  <Edit className="w-4 h-4" />
                </button>
                <button className="p-2 text-discord-light hover:text-discord-red transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {characters.length === 0 && (
          <div className="text-center py-8">
            <User className="w-12 h-12 text-discord-light/40 mx-auto mb-4" />
            <p className="text-discord-light">No characters yet</p>
            <p className="text-discord-light/60 text-sm mt-2">
              Create your first character to get started with Storybook
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
